import { type SiteResponse } from '@on3/api/src';
import { useModal } from '@on3/ui-lib/components/Modal/Provider';
import { ServiceLogo } from '@on3/ui-lib/components/ServiceLogo/ServiceLogo';
import { useSubscribe } from '@on3/ui-lib/components/Subscription/Provider';
import { useSite } from '@on3/ui-lib/index';
import { Join } from '@on3/ui-lib/src/components/Subscription/Templates/Join';
import { type AltPromoType, type PromoType } from '@on3/ui-lib/types/purchase';
import { parseTemplateVariables } from '@on3/ui-lib/utils/parsers';
import clsx from 'clsx';
import { useMemo } from 'react';

import styles from './JoinHero.module.scss';

interface JoinHeroProps {
  site: SiteResponse | null;
  promo?: PromoType | AltPromoType;
  teamSelect?: boolean;
}

export const JoinHero = ({
  site: selectedSite,
  promo,
  teamSelect = false,
}: JoinHeroProps) => {
  const { showModal } = useModal();
  const { currentSite } = useSite();

  const isTeam = currentSite.isTeam;
  const isChannel = currentSite.isChannel;

  const site = selectedSite ? selectedSite : currentSite;
  const siteOrg = site.organization;
  const siteName = site.name;

  const isNational = selectedSite?.isNational;

  const showName = (teamSelect && !isNational) || isTeam || isChannel;

  const joinType = useMemo(() => {
    if (['join', 'winback', 'intro', 'gift'].includes(promo || 'join')) {
      return promo;
    }

    return 'join';
  }, [promo]);

  const {
    // @ts-expect-error TODO: add intro type to api
    templates: { [joinType]: template },
  } = useSubscribe();

  const subheading = template?.data.title ? (
    parseTemplateVariables(template.data.title, {
      SITE_NAME: siteName || '',
      ORG_NAME: siteOrg?.knownAs || siteOrg?.name || '',
      ORG_MASCOT: siteOrg?.mascot || '',
    })
  ) : (
    <>
      <strong>One subscription:</strong> The best{' '}
      {siteOrg?.knownAs || siteOrg?.name || siteName} coverage and community,
      and all access to the On3 College Sports Network.
    </>
  );

  return (
    <div className={styles.block}>
      <div className={styles.container}>
        <header
          className={clsx(styles.header, {
            [styles.noName]: !showName,
            [styles.team]: isTeam,
          })}
        >
          <div className={styles.titleBlock}>
            {showName && (
              <h1>
                {isTeam || isChannel
                  ? currentSite.name
                  : selectedSite?.name || 'Your Team'}
              </h1>
            )}
            <div className={styles.logos}>
              <div className={styles.logo}>
                <ServiceLogo className={styles.on3} type="on3" />
              </div>
              <div className={styles.logo}>
                <ServiceLogo className={styles.rivals} type="rivals" />
              </div>
            </div>
          </div>
          <p className={styles.subheading}>{subheading}</p>
        </header>
        {!teamSelect && (
          <div className={styles.offers}>
            <Join
              handleJoin={(props) => showModal('user.join', props, 'screen')}
              joinType={joinType}
              props={{
                'data-background': 'theme',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
